import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { GraphAuthService } from '@app/modules/microsoft-graph/auth/services/graph-auth.service';
import { CalendarService } from '@app/modules/microsoft-graph/services/graph-calendar.service';
import { GraphUserService } from '@app/modules/microsoft-graph/services/graph-user.service';
import { ActivityTypeGet, ActivityTypeService } from '@app/services/activity-type.service';
import { CustactEditGet, CustactProjectItem, CustactService, RecurrenceItem } from '@app/services/custact.service';
import { DeviationService, DeviationSourceItem, DeviationTypeItem } from '@app/services/deviation.service';
import { ActivityStatusItem, ActivityTypeItem, EconomyService, SalaryTypeItem } from '@app/services/economy.service';
import { EquipmentItem, EquipmentService } from '@app/services/equipment.service';
import { LabourerItem, LabourService } from '@app/services/labour.service';
import { BusinesscoItem, DepartmentItem, LogisticService } from '@app/services/logistic.service';
import { ProjectProcessItem, ProjectProcessService } from '@app/services/project-process.service';
import { SalesPossibilityItem, SalesService, SalesSizeItem } from '@app/services/sales.service';
import { CustomerItem, ProductKeynameItem, SearchOrderItem, SearchPersonItem, ServiceObjectItem, TypeaheadService } from '@app/services/typeahead.service';
import { AccountInfo } from '@azure/msal-browser';
import { Event } from '@microsoft/microsoft-graph-types';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'tt-custact-edit',
    templateUrl: './custact-edit.component.html',
    styleUrls: ['./custact-edit.component.css'],
})
export class CustactEditComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public params?: { custact_no: string; filterfield: string; filtervalue: string };

    public get?: CustactEditGet;

    @Output()
    public submit = new EventEmitter<CustactEditGet>();

    public activityType?: ActivityTypeGet;

    public activityTypes: ActivityTypeItem[] = [];

    public activityStatuses: ActivityStatusItem[] = [];

    public deviationTypes: DeviationTypeItem[] = [];

    public recurrenceOptions: RecurrenceItem[] = [];

    public projects: CustactProjectItem[] = [];

    public projectProcesses: ProjectProcessItem[] = [];

    public equipments: EquipmentItem[] = [];

    public deviationSources: DeviationSourceItem[] = [];

    public deviationSources2: DeviationSourceItem[] = [];

    public salesPossibilities: SalesPossibilityItem[] = [];

    public salesSizes: SalesSizeItem[] = [];

    public salaryTypes: SalaryTypeItem[] = [];

    public companies: BusinesscoItem[] = [];

    public departments: DepartmentItem[] = [];

    public saving = false;

    public grid?: GridOptions;

    public account?: AccountInfo | null;

    private destroy$ = new Subject<void>();

    constructor(
        private custact: CustactService,
        private deviation: DeviationService,
        private economy: EconomyService,
        private activityTypeService: ActivityTypeService,
        private projectProcess: ProjectProcessService,
        private labour: LabourService,
        private equipment: EquipmentService,
        private sales: SalesService,
        private logistic: LogisticService,
        private typeahead: TypeaheadService,
        private modal: ModalService,
        public graphAuth: GraphAuthService,
        private graphUser: GraphUserService,
        private calendar: CalendarService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
        console.log(this.navigator);
        graphAuth.account$.pipe(takeUntil(this.destroy$)).subscribe({
            next: (account) => {
                this.account = account;
            },
        });
    }

    public searchCustomers = () => {
        return this.typeahead.searchCustomer({ cust_name: this.get?.cust_name ?? '', custtype: this.get?.custtype ?? 'C', lastselect: 'SEARCH' });
    };

    public searchServiceObjects = () => {
        return this.typeahead.searchServiceObjects({ searchtext: this.get?.serviceobject_name ?? '' });
    };

    public searchPersons = () => {
        return this.typeahead.searchPersons({ pers_name: this.get?.initiated_by_person_name ?? '' });
    };

    public searchLabourers = () => {
        return this.labour.getLabourers({ searchtext: this.get?.responsible_labour_name ?? '' });
    };

    public searchOrders = () => {
        return this.typeahead.searchOrders({ searchtext: this.get?.order_order_no ?? '', cust_no: this.get?.cust_no ?? '0' });
    };

    public searchProducts = () => {
        return this.typeahead.searchProductsKeyname({ searchtext: this.get?.prod_keyname ?? '' });
    };

    public async onActivityTypeChanged() {
        if (!this.get) return;

        this.loadActivityStatuses();
        this.loadDeviationTypes();
        await this.loadActivityType();
        this.get.is_deviation = this.activityType!.is_activitytype_no_deviation;
        this.get.is_sales_opportunity = this.activityType!.is_sales_opportunity;
    }

    public onCustomerSelected(event?: CustomerItem) {
        if (!this.get) return;

        this.get.cust_name = event?.item_name ?? '';
        this.get.cust_no = event?.item_id ?? '0';

        this.loadProjects();
    }

    public onServiceObjectSelected(event?: ServiceObjectItem) {
        if (!this.get) return;

        this.get.serviceobject_keyno = event?.item_id ?? '0';
        this.get.serviceobject_name = event?.item_name ?? '';
    }

    public onProjectChanged() {
        this.loadProjectProcesses();
    }

    public onInitiatedBySelected(event?: SearchPersonItem) {
        if (!this.get) return;

        this.get.initiated_by_person = event?.item_id ?? '0';
        this.get.initiated_by_person_name = event?.item_name ?? '';
    }

    public onResponsibleLabourerSelected(event?: LabourerItem) {
        if (!this.get) return;

        this.get.responsible_labour_no = event?.item_id ?? '0';
        this.get.responsible_labour_name = event?.item_name ?? '';
    }

    public onOrderSelected(event?: SearchOrderItem) {
        if (!this.get) return;

        this.get.order_order_no = event?.order_order_no ?? '';
        this.get.order_internal_no = event?.order_internal_no ?? '0';
        this.get.cust_no = event?.cust_no ?? '0';
        this.get.cust_name = event?.cust_name ?? '';

        this.loadProjects();
        this.readGrid();
    }

    public onActivityStatusSelected(event: ActivityStatusItem | null) {
        if (!this.get) return;

        this.get.actstatus_keyno = event?.actstatus_keyno ?? '0';
        this.get.actstatus_keyno_label = event?.actstatus_name ?? '';

        if (event?.set_ok === '1') {
            this.get.ok = '1';
        }
    }

    public onDeviationTypeChanged() {
        if (!this.get) return;

        this.loadDeviationSources();
    }

    public onDeviationSourceChanged() {
        if (!this.get) return;

        this.loadDeviationSources2();
    }

    public onProductSelected(event?: ProductKeynameItem) {
        if (!this.get) return;

        this.get.prod_id = event?.prod_id ?? '';
        this.get.prod_keyname = event?.prod_keyname ?? '';
    }

    public onBusinessCoChanged() {
        if (!this.get) return;

        this.loadDepartments();
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            const { errorcode, errormessage, custact_no } = await this.custact.saveCustactEdit({ ...this.get, deviation_date: this.get.deviation_date || '1900-01-01', custactproducts: this.grid?.gridfunc?.getAllRows() });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                if (this.activityType?.sync_with_microsoft_365 === '1') {
                    await this.addEventToMicrosoftCalendar(custact_no);
                }

                this.submit.emit({ ...this.get, custact_no: custact_no });
                this.navigator?.back(undefined, { ...this.get, custact_no: custact_no });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    private readGrid() {
        if (!this.grid) {
            this.grid = new Grid()
                .setRememberId('orderlineproducts_grid')
                .setLoadDataFunction({ method: 2773, parameters: () => ({ order_internal_no: this.get?.order_internal_no ?? '', custact_no: this.get?.custact_no ?? '' }) })
                .setKendoConfig({ height: null });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    private async addEventToMicrosoftCalendar(custact_no: string) {
        if (!this.get) return;

        const account = await this.graphAuth.getAccount();
        const token = await this.graphAuth.getAccessToken(account);
        const user = await this.graphUser.getUser(account.localAccountId, token);

        const startDate = new Date(this.get.datetime_start).toISOString();
        const endDate = new Date(this.get.datetime_end).toISOString();

        const event: Event = {
            subject: this.get.note,
            body: {
                content: this.get.cust_name,
                contentType: 'html',
            },
            bodyPreview: this.get.cust_name,
            start: {
                dateTime: startDate,
                timeZone: 'Europe/Berlin',
            },
            end: {
                dateTime: endDate,
                timeZone: 'Europe/Berlin',
            },
        };

        if ('' + this.get.custact_no === '0') {
            event.location = {
                displayName: '',
            };

            event.attendees = [
                {
                    emailAddress: {
                        address: user.mail,
                        name: user.displayName,
                    },
                    type: 'required',
                },
            ];

            const response = await this.calendar.createEvent(event, { token: token });
            await this.custact.saveCustactEdit({ state: 'update_graph_event_id', custact_no: custact_no, event_id: response.id });
        } else {
            await this.calendar.updateEvent(event, { eventId: this.get.microsoft_graph_event_id, token: token });
        }
    }

    public async loadGet() {
        if (!!this.params && typeof this.params === 'object' && 'custact_no' in this.params) {
            this.get = await this.custact.getCustactEdit({ custact_no: this.params.custact_no, connectto: this.params.filterfield, connectto_value: this.params.filtervalue });
            this.cdr.markForCheck();
        }
    }

    private async loadActivityType() {
        if (!!this.get) {
            this.activityType = await this.activityTypeService.getActivityType({ activitytype_no: this.get.activitytype_no });
        }
    }

    private async loadActivityTypes() {
        this.activityTypes = await this.economy.getActivityTypes();
    }

    private async loadActivityStatuses() {
        if (!!this.get) {
            this.activityStatuses = await this.economy.getActivityStatuses({ activitytype_no: this.get.activitytype_no });
        }
    }

    private async loadRecurrenceOptions() {
        this.recurrenceOptions = await this.custact.getRecurrenceOptions();
    }

    private async loadProjects() {
        if (!!this.get) {
            this.projects = await this.custact.getProjects({ cust_no: this.get.cust_no, custact_no: this.get.custact_no });
        }
    }

    private async loadProjectProcesses() {
        if (!!this.get) {
            this.projectProcesses = await this.projectProcess.getProjectProcesses({ project_keyno: this.get.project_keyno });
        }
    }

    private async loadEquipments() {
        this.equipments = await this.equipment.getEquipmentList();
    }

    private async loadDeviationTypes() {
        if (!!this.get) {
            this.deviationTypes = await this.deviation.getDeviationTypes({ isactive: '1', activitytype_no: this.get.activitytype_no });
        }
    }

    private async loadDeviationSources() {
        if (!!this.get) {
            this.deviationSources = await this.deviation.getDeviationSources({ deviationtype_keyno: this.get.deviationtype_keyno });
        }
    }

    private async loadDeviationSources2() {
        if (!!this.get) {
            this.deviationSources2 = await this.deviation.getDeviationSources2({ deviationsource_keyno: this.get.deviationsource_keyno });
        }
    }

    private async loadSalesPossibilities() {
        this.salesPossibilities = await this.sales.getSalesPossibilities();
    }

    private async loadSalesSizes() {
        this.salesSizes = await this.sales.getSalesSizes();
    }

    private async loadSalaryTypes() {
        this.salaryTypes = await this.economy.getSalaryTypes();
    }

    private async loadCompanies() {
        this.companies = await this.logistic.getCompanies();
    }

    private async loadDepartments() {
        if (!!this.get) {
            this.departments = await this.logistic.getDepartments(this.get.businessco_no);
        }
    }

    private async init() {
        await this.loadGet();
        this.navigator?.setHeading?.(this.get?.page_heading ?? '');
        this.loadActivityStatuses();
        this.loadProjects();
        this.loadProjectProcesses();
        this.loadDeviationTypes();
        this.loadDeviationSources();
        this.loadDeviationSources2();
        this.loadDepartments();
        await this.loadActivityType();
        this.readGrid();
    }

    private async staticOnInit() {
        this.navigator?.setSize?.({ width: '98vw', height: '95vh' });
        this.loadActivityTypes();
        this.loadRecurrenceOptions();
        this.loadEquipments();
        this.loadSalesPossibilities();
        this.loadSalesSizes();
        this.loadSalaryTypes();
        this.loadCompanies();
    }

    ngOnInit(): void {
        this.staticOnInit();

        if (!!this.navigator) {
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
